import { useEffect } from 'react';
import {
  Box,
  CircularProgress,
  FormHelperText,
  Autocomplete,
  TextField,
  Button,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { ApiError, getErrorMessage } from '@pro4-erp/core-web';
import { useTranslation } from 'react-i18next';
import { getPortalUserRegistrationById } from '../api/getPortalUserRegistrationById';
import { FinishRegistrationFormType, PortalUserRegistration } from '../types';
import { ErrorCard, PortalUserConnection } from '@/common';
import { finishRegistration } from '../api/finishRegistration';
import { useAuthStore } from '@/core';

const Page = () => {
  const authStore = useAuthStore();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const params = useParams<{ registrationUrlId: string }>() as { registrationUrlId: string };

  const form = useForm<FinishRegistrationFormType>({
    defaultValues: {
      password: '',
      confirmPassword: '',
      defaultConnection: null,
    },
  });

  const query = useQuery<PortalUserRegistration, ApiError>({
    queryKey: ['portal-user-registration-id', params.registrationUrlId],
    queryFn: () => getPortalUserRegistrationById({ registrationUrlId: params.registrationUrlId }),
    retry: false,
  });

  const finishRegistrationMutation = useMutation({
    mutationFn: finishRegistration,
    onSuccess: (data) => {
      authStore.authenticateUser(data.user);
      localStorage.setItem('accessToken', data.accessToken);
      enqueueSnackbar(
        t('Successfully created an account.', { defaultValue: 'Successfully created an account.' }),
        { variant: 'success' }
      );
    },
    onError: (error: ApiError) => {
      form.setError('root', { message: getErrorMessage(error.errorResponseDto.message) });
    },
  });

  useEffect(() => {
    if (!query.isLoading && query.data) {
      form.setValue('defaultConnection', query.data.connections[0], {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [form, query.data, query.isLoading]);
  const onSubmit = (data: FinishRegistrationFormType) => {
    if (!query.data) {
      return;
    }

    form.clearErrors();

    if (!data.defaultConnection) {
      form.setError('defaultConnection', {
        message: t('Default connection is required.', {
          defaultValue: 'Default connection is required.',
        }).toString(),
      });
      return;
    }

    finishRegistrationMutation.mutate({
      data: {
        password: data.password,
        confirmPassword: data.confirmPassword,
        defaultConnectionId: data.defaultConnection.id,
        registrationUrlId: query.data.urlId,
      },
    });
  };

  const onInvalid = () => {
    if (!form.getValues().defaultConnection) {
      form.setError('defaultConnection', {
        message: t('Default connection is required.', {
          defaultValue: 'Default connection is required.',
        }).toString(),
      });
    }
  };

  return (
    <>
      {query.isLoading && <CircularProgress />}
      {query.error && <ErrorCard error={query.error} onRetryClick={() => query.refetch()} />}
      {query.data && (
        <Box
          component="form"
          autoComplete="chrome-off"
          onSubmit={form.handleSubmit(onSubmit, onInvalid)}
          noValidate
          sx={{ width: '100%' }}
        >
          <TextField
            label={t('Email', { defaultValue: 'Email' })}
            defaultValue={query.data.email}
            disabled
            fullWidth
            sx={{
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: '#888888',
              },
            }}
          />
          {!!query.data.connections && (
            <Autocomplete
              value={form.getValues().defaultConnection}
              onChange={(_, newValue: PortalUserConnection | null) => {
                form.setValue('defaultConnection', newValue, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
                if (!newValue) {
                  form.setError('defaultConnection', {
                    message: t('Default connection is required.', {
                      defaultValue: 'Default connection is required.',
                    }).toString(),
                  });
                } else {
                  form.clearErrors('defaultConnection');
                }
              }}
              options={query.data.connections}
              fullWidth
              getOptionLabel={(e) => `${e.showAs} - ${e.type}`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              sx={{ mt: '1rem' }}
              renderInput={(inputParams) => (
                <TextField
                  {...inputParams}
                  InputLabelProps={{}} // Added to fix typeerror
                  autoComplete="one-time-code"
                  label={t('Default connection', { defaultValue: 'Default connection' })}
                  error={!!form.formState.errors.defaultConnection}
                  helperText={form.formState.errors.defaultConnection?.message?.toString()}
                />
              )}
            />
          )}
          <Controller
            control={form.control}
            name="password"
            rules={{
              required: t('Password is required.', {
                defaultValue: 'Password is required.',
              }).toString(),
              minLength: {
                value: 6,
                message: t('Must be at least 6 characters long.', {
                  defaultValue: 'Must be at least 6 characters long.',
                }).toString(),
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                sx={{ mt: '1rem' }}
                label={t('Password', { defaultValue: 'Password' })}
                type="password"
                fullWidth
                autoComplete="one-time-code"
                error={!!form.formState.errors.password}
                helperText={form.formState.errors.password?.message?.toString()}
              />
            )}
          />
          <Controller
            control={form.control}
            name="confirmPassword"
            rules={{
              required: 'Confirm password is required.',
              validate: (value) => {
                if (form.watch('password') !== value) {
                  return t('Passwords do not match.', {
                    defaultValue: 'Passwords do not match.',
                  }).toString();
                }

                return undefined;
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                sx={{ mt: '1rem' }}
                label={t('Confirm password', { defaultValue: 'Confirm password' })}
                type="password"
                fullWidth
                autoComplete="one-time-code"
                error={!!form.formState.errors.confirmPassword}
                helperText={form.formState.errors.confirmPassword?.message?.toString()}
              />
            )}
          />
          {!!form.formState.errors.root && (
            <FormHelperText error>{form.formState.errors.root.message?.toString()}</FormHelperText>
          )}
          <Button
            type="submit"
            variant="contained"
            sx={{ mt: '1rem' }}
            disabled={finishRegistrationMutation.isLoading}
            fullWidth
          >
            {t('Submit', { defaultValue: 'Submit' })}
          </Button>
        </Box>
      )}
    </>
  );
};

export default Page;
